// Analytics Audience Profile - New Listeners Data Component
// Handles new listeners data with date range filtering

import flatpickr from "flatpickr";

document.addEventListener('alpine:init', () => {
  
  Alpine.data('newListenersData', () => ({
    loading: false,
    total: 0,
    error: null,
    dateRange: 'all_time',
    startDate: null,
    endDate: null,
    datePicker: null,
    isInitialLoad: true, // Track if this is the first load
    
    init() {
      this.show_id = this.$refs.newListenersSection.dataset.showId;
      this.download_base_url = this.$refs.newListenersSection.dataset.downloadBaseUrl || (this.show_id ? `/shows/${this.show_id}/downloads/new-listeners.csv` : '#');
      this.download_url = this.download_base_url;
      
      // Check if we're in demo mode (download_base_url is '#' or show_id is empty)
      this.isDemoMode = this.download_base_url === '#' || !this.show_id;
      
      this.initializeDatePicker();
      this.setupButtonListeners();
      this.setActiveButton('all_time');
      this.updateDownloadUrl();
      this.loadData();
    },
    
    initializeDatePicker() {
      // Skip flatpickr initialization in demo mode
      if (this.isDemoMode) {
        return;
      }
      
      // Get min date from data attribute set by backend
      const minDate = this.$refs.newListenersSection.dataset.minDate || "2024-10-14";
      
      // Initialize flatpickr for custom date range
      this.datePicker = flatpickr("#new-listeners-range", {
        mode: "range",
        maxDate: "today",
        minDate: minDate,
        dateFormat: "M j, Y",
        onChange: (selectedDates) => {
          if (selectedDates.length === 2) {
            this.startDate = selectedDates[0];
            this.endDate = selectedDates[1];
            this.dateRange = 'custom';
            this.setActiveButton('custom');
            this.updateDateRangeDisplay();
            this.updateDownloadUrl(); // Update download URL when custom date range is selected
            this.loadData();
          }
        }
      });
    },
    
    setupButtonListeners() {
      // Skip button listeners in demo mode (buttons use Alpine directives instead)
      if (this.isDemoMode) {
        return;
      }
      
      const buttons = this.$refs.newListenersSection.querySelectorAll('[data-range]');
      buttons.forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          const range = button.dataset.range;
          
          if (range === 'custom') {
            // Open the flatpickr calendar
            if (this.datePicker) {
              this.datePicker.open();
            }
          } else {
            this.handleDateRangeChange({ type: range === 'all_time' ? 'all_time' : 'preset', days: range });
          }
        });
      });
    },
    
    setActiveButton(activeRange) {
      // Skip in demo mode (buttons don't have data-range attributes)
      if (this.isDemoMode) {
        return;
      }
      
      const buttons = this.$refs.newListenersSection.querySelectorAll('[data-range]');
      buttons.forEach(button => {
        const range = button.dataset.range;
        
        // Remove all active classes
        button.classList.remove('bg-quill-active', 'text-white', 'hover:bg-purple-700');
        button.classList.add('bg-white', 'text-gray-700', 'border', 'border-gray-300', 'hover:bg-quill-activenav', 'hover:text-quill-selected');
        
        // Add active classes to the selected button
        if (range === activeRange) {
          button.classList.remove('bg-white', 'text-gray-700', 'border', 'border-gray-300', 'hover:bg-quill-activenav', 'hover:text-quill-selected');
          button.classList.add('bg-quill-active', 'text-white', 'hover:bg-purple-700');
        }
      });
    },
    
    handleDateRangeChange(payload) {
      this.error = null;
      this.isInitialLoad = false; // User is making a selection, not initial load anymore
      
      switch (payload.type) {
        case 'all_time':
          this.dateRange = 'all_time';
          this.startDate = null;
          this.endDate = null;
          this.setActiveButton('all_time');
          this.updateDateRangeDisplay('All Time');
          break;
          
        case 'preset':
          this.dateRange = 'preset';
          this.startDate = payload.days;
          this.endDate = null;
          this.setActiveButton(payload.days.toString());
          this.updateDateRangeDisplay(`${payload.days} Days`);
          break;
          
        case 'custom':
          this.dateRange = 'custom';
          this.startDate = payload.from;
          this.endDate = payload.to;
          this.setActiveButton('custom');
          this.updateDateRangeDisplay(`${this.formatDate(payload.from)} - ${this.formatDate(payload.to)}`);
          break;
      }
      
      this.updateDownloadUrl(); // Update download URL when date range changes
      this.loadData();
    },
    
    updateDateRangeDisplay(displayText = null) {
      const input = document.getElementById('new-listeners-range');
      if (input) {
        if (displayText) {
          input.value = displayText;
        } else if (this.dateRange === 'custom' && this.startDate && this.endDate) {
          input.value = `${this.formatDate(this.startDate)} - ${this.formatDate(this.endDate)}`;
        } else {
          input.value = '';
        }
      }
    },
    
    formatDate(date) {
      if (typeof date === 'string') {
        date = new Date(date);
      }
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    },
    
    updateDownloadUrl() {
      if (this.dateRange === 'custom' && this.startDate && this.endDate) {
        // For custom date ranges, append the date parameters
        const startDateStr = this.formatDateForAPI(this.startDate);
        const endDateStr = this.formatDateForAPI(this.endDate);
        this.download_url = `${this.download_base_url}?beginning_date=${startDateStr}&end_date=${endDateStr}`;
      } else if (this.dateRange === 'preset' && this.startDate) {
        // For preset ranges (like "Last 7 days"), calculate the date range
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - parseInt(this.startDate));
        const startDateStr = this.formatDateForAPI(startDate);
        const endDateStr = this.formatDateForAPI(endDate);
        this.download_url = `${this.download_base_url}?beginning_date=${startDateStr}&end_date=${endDateStr}`;
      } else {
        // For all time, use the base URL without parameters
        this.download_url = this.download_base_url;
      }
    },
    
    async loadData() {
      this.loading = true;
      this.error = null;
      
      try {
        const params = new URLSearchParams({
          date_range_type: this.dateRange
        });
        
        if (this.startDate) {
          params.append('start_date', this.formatDateForAPI(this.startDate));
        }
        
        if (this.endDate) {
          params.append('end_date', this.formatDateForAPI(this.endDate));
        }
        
        const baseUrl = this.show_id 
          ? `/shows/${this.show_id}/analytics/new_listeners_data` 
          : `/analytics/new_listeners_data`;
        const url = `${baseUrl}?${params}`;
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        
        if (data.error) {
          if (this.dateRange === 'custom' && !this.isInitialLoad) {
            this.error = data.error;
          }
          this.total = 0;
        } else {
          this.total = data.total || 0;
          this.error = null;
        }
        
      } catch (error) {
        if (this.dateRange === 'custom' && !this.isInitialLoad) {
          this.error = 'Failed to load data';
        }
        this.total = 0;
      } finally {
        this.loading = false;
        if (this.isInitialLoad) {
          this.isInitialLoad = false;
        }
      }
    },
    
    formatDateForAPI(date) {
      if (typeof date === 'string') {
        return date;
      }
      return date.toISOString().split('T')[0]; // YYYY-MM-DD format
    },
    
    get displayTotal() {
      if (this.loading) {
        return 'loading data';
      }
      
      // Always show N/A if total is 0, regardless of error state
      if (this.total === 0) {
        return 'N/A';
      }
      
      return this.total.toLocaleString();
    },
    
    get showError() {
      // Only show error for custom date ranges when there's no data
      return this.error && this.dateRange === 'custom' && this.total === 0;
    },
    
    get showLoading() {
      return this.loading;
    }
  }));
});

